import type { UserRoleTypeDto } from "@/api/types";
import {
  canListAddresses,
  canListAnySurvey,
  canListDocuments,
  canListMessages,
  canListUsers,
  canManageAllDocuments,
  canManageBuildings,
  canManageCompanies,
  canManageProjectManagement,
  canManageRoles,
  canViewProjectManagement,
  canViewSchedule,
} from "@/helpers/permissions";
import type { PermissionSelector } from "@/hooks/usePermission";

export const canAccessHome: PermissionSelector = (x) => x.isAdmin && !x.isMaintenance && !x.isServicePartner;

export const canAccessAnalytics: PermissionSelector = (x) => x.isAdmin && !x.isCaretaker && !x.isServicePartner;

/** Whether a user may view safe aggregate analytics on an operational page. */
export const canAccessOperationalAnalyticsSummary: PermissionSelector = (x) => x.isAdmin;

/** Whether a project role may view safe aggregate analytics for that project. */
export const canRoleTypeAccessOperationalAnalyticsSummary = (roleType: UserRoleTypeDto): boolean =>
  roleType !== "resident";

/** Whether a project role may open the project home. Mirrors `canAccessHome` for code that only knows the role. */
export const canRoleTypeAccessHome = (roleType: UserRoleTypeDto): boolean =>
  roleType !== "resident" && roleType !== "maintenance" && roleType !== "servicePartner";

/** Whether a project role may open detailed analytics for that project. */
export const canRoleTypeAccessAnalytics = (roleType: UserRoleTypeDto): boolean =>
  roleType !== "resident" && roleType !== "caretaker" && roleType !== "servicePartner";

export const canAccessNeighbours: PermissionSelector = (x) => canListUsers(x);

export const canAccessAddresses: PermissionSelector = (x) => x.isAdmin && canListAddresses(x);

export const canAccessCompanies = canManageCompanies;

export const canAccessInterestGroups: PermissionSelector = () => true;

export const canAccessHelpCategories: PermissionSelector = () => true;

export const canAccessCommunityFeed = canListMessages;

export const canAccessChats = canListUsers;

export const canAccessCalendar: PermissionSelector = () => true;

export const canAccessServices: PermissionSelector = (x) => x.servicePartners.canListPages;

export const canAccessAssets = canViewSchedule;

export const canAccessResidentTickets: PermissionSelector = (x) => x.isResident && x.canCreateTicket;

export const canAccessAdminTickets: PermissionSelector = (x) => x.isAdmin && !x.isServicePartner;

/** Whether a project role may open the admin ticket list. Mirrors `canAccessAdminTickets` for code that only knows the role. */
export const canRoleTypeAccessAdminTickets = (roleType: UserRoleTypeDto): boolean =>
  roleType !== "resident" && roleType !== "servicePartner";

export const canAccessPracticalAndMore: PermissionSelector = (x) => canListDocuments(x) || canManageAllDocuments(x);

export const canAccessRegularSurveys = canListAnySurvey;

export const canAccessBuildings: PermissionSelector = (x) => canManageBuildings(x);

export const canAccessProjectSettings: PermissionSelector = (x) => x.isSuperAdmin;

export const canAccessProjectStyling: PermissionSelector = (x) =>
  canViewProjectManagement(x) || canManageProjectManagement(x);

export const canAccessProjectOnboadingScreens: PermissionSelector = (x) =>
  canViewProjectManagement(x) || canManageProjectManagement(x);

export const canAccessProjectPlusButtonOptions: PermissionSelector = (x) =>
  canViewProjectManagement(x) || canManageProjectManagement(x);

export const canAccessProjectQuickActions: PermissionSelector = (x) =>
  canViewProjectManagement(x) || canManageProjectManagement(x);

export const canAccessProjectRolesAndPermissions: PermissionSelector = (x) => canManageRoles(x);

export const canAccessProjectIntegrationSettings: PermissionSelector = (x) =>
  x.integrations.canManageZiezodan ||
  x.integrations.canManageILoq ||
  x.integrations.canManageMyPup ||
  x.integrations.canManageBringme ||
  x.integrations.canManageDico ||
  x.integrations.canManageSwoogo ||
  x.integrations.canManageCopilot ||
  x.integrations.canManageIqbi ||
  x.integrations.canManageHomeDna ||
  x.integrations.canManageRems ||
  x.integrations.canManageHealthDistrict ||
  x.integrations.canManageOfficeRnd ||
  x.integrations.canManageHubSpot ||
  x.integrations.canManageOutlook ||
  x.integrations.canManageJim ||
  x.integrations.canManageJansenHuybregts ||
  x.integrations.canManageProjectApiAccess;

export const canAccessAdminNotifications: PermissionSelector = (x) => x.isAdmin;

/** Allows notification-center access only for users with the resident role. */
export const canAccessResidentNotifications: PermissionSelector = (x) => x.isResident;
